/**
 * Authentication Hook for Support MiniApp
 * Handles deviceId-based silent auth via SuperApp bridge.
 * Known device → 200 → authenticated.
 * Unknown device → 401 → deviceNotAuthorized error state.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import { bridgeService } from '../services/bridge';
import type { AuthState, AuthUser } from '../types';

interface UseAuthReturn extends AuthState {
  logout: () => Promise<void>;
}

const AUTH_TOKEN_KEY = 'support_auth_token';
const AUTH_USER_KEY = 'support_auth_user';

// Shared ref so React Strict Mode double-mount doesn't fire two /register calls
const initPromiseRef = { current: null as Promise<void> | null };

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    isInSuperApp: false,
    deviceNotAuthorized: false,
  });

  // Initialize authentication on mount (single-flight: avoid duplicate register on Strict Mode remount)
  useEffect(() => {
    if (initPromiseRef.current) {
      initPromiseRef.current.finally(() => {
        initPromiseRef.current = null;
      });
      return;
    }

    const initAuth = async () => {
      try {
        // Step 1: Initialize DG SuperApp bridge
        const initData = await bridgeService.initialize();
        const isInSuperApp = initData?.isDGSuperApp ?? false;

        if (!isInSuperApp) {
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            isInSuperApp: false,
            deviceNotAuthorized: false,
          });
          return;
        }

        // Step 2: Check existing valid JWT session
        const savedUser = localStorage.getItem(AUTH_USER_KEY);
        if (savedUser && localStorage.getItem(AUTH_TOKEN_KEY)) {
          const verified = await apiClient.verifySession();
          if (verified) {
            try {
              const cached = JSON.parse(savedUser) as AuthUser;
              // Merge the fresh fields from /verify into the cached user so
              // server-side role changes (Phase 34: L1→L2 elevations) take
              // effect on app reopen instead of waiting for the 1-hour JWT TTL
              // to lapse and force a full /register. deviceId is intentionally
              // left as the cached value (it's the JWT subject; if the device
              // were unknown, /verify would have returned null already).
              const user: AuthUser = {
                ...cached,
                profileId: verified.profileId,
                username: verified.username,
                level: verified.level,
                telegramUserId: verified.telegramUserId ?? cached.telegramUserId ?? null,
              };
              localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
              setState({
                isAuthenticated: true,
                isLoading: false,
                user,
                isInSuperApp: true,
                deviceNotAuthorized: false,
              });
              return;
            } catch {
              // Invalid saved user JSON — clear and fall through to re-register
              localStorage.removeItem(AUTH_USER_KEY);
              localStorage.removeItem(AUTH_TOKEN_KEY);
            }
          }
        }

        // Step 3: Extract deviceId from bridge
        const deviceId = await bridgeService.extractDeviceId();

        if (!deviceId) {
          // Cannot determine authorization without a deviceId
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            isInSuperApp: true,
            deviceNotAuthorized: false,
          });
          return;
        }

        // Step 4: Call register with deviceId + the raw signed initData blob.
        // The server verifies the initData HMAC in log-only mode (it does not
        // gate JWT issuance yet), so this stays backward-compatible while we
        // confirm real-device blobs verify correctly in the logs.
        const rawInitData = await bridgeService.getRawInitData();
        const result = await apiClient.register(deviceId, rawInitData ?? undefined);

        if (result.success && result.data) {
          const user: AuthUser = {
            deviceId,
            profileId: result.data.user.profileId,
            username: result.data.user.username,
            level: result.data.user.level,
            isTechnician: true,
            telegramUserId: result.data.user.telegramUserId ?? null,
          };
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
          setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            isInSuperApp: true,
            deviceNotAuthorized: false,
          });
          return;
        }

        // Register failed — check if device is explicitly not authorized
        const errorMsg = result.error ?? '';
        const isNotAuthorized =
          /device not authorized/i.test(errorMsg) ||
          errorMsg.includes('401');

        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          isInSuperApp: true,
          deviceNotAuthorized: isNotAuthorized,
        });
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          isInSuperApp: false,
          deviceNotAuthorized: false,
        });
      }
    };

    const promise = initAuth();
    initPromiseRef.current = promise;
    promise.finally(() => {
      initPromiseRef.current = null;
    });
  }, []);

  // Logout — Phase 35. Awaits the API call (which is best-effort
  // and swallows network errors internally), then clears local
  // user state, then triggers the App.tsx re-render that bounces
  // back to the auth-entry / SuperApp Required branch. No router
  // navigation needed — the conditional render IS the bounce.
  const logout = useCallback(async (): Promise<void> => {
    await apiClient.logout();
    localStorage.removeItem(AUTH_USER_KEY);

    setState(prev => ({
      ...prev,
      isAuthenticated: false,
      user: null,
      deviceNotAuthorized: false,
    }));
  }, []);

  return {
    ...state,
    logout,
  };
}
